import { SortDirection } from "@redotech/congo-enums/sort-direction";
import { OpensearchPaginationCursorSchema } from "@redotech/redo-views-common/table";
import * as z from "zod";

export const inboundDashboardReturnView = [
  "in_transit",
  "coming",
  "delivered_today",
  "waiting",
  "missing",
  "sorted_today",
] as const;
export type InboundDashboardReturnView =
  (typeof inboundDashboardReturnView)[number];

export const INBOUND_DASHBOARD_RETURNS_MAX_PAGE_SIZE = 50;
export type InboundDashboardReturnsCursor = z.infer<
  typeof OpensearchPaginationCursorSchema
>;

export const inboundWaitingAgeBucket = [
  "under_6h",
  "h6_12",
  "h12_24",
  "over_24h",
] as const;
export type InboundWaitingAgeBucket = (typeof inboundWaitingAgeBucket)[number];

export const inboundComingGroup = ["past_due", "later", "no_estimate"] as const;

/** A calendar day (`YYYY-MM-DD`) or one of the grouped buckets. */
export const inboundComingKeySchema = z.union([
  z.enum(inboundComingGroup),
  z.iso.date(),
]);
export type InboundComingKey = z.infer<typeof inboundComingKeySchema>;

/** Whether the warehouse can flag a missing return, by the same rule as the return page's Flag action. */
const inboundFlagState = ["available", "flagged", "unavailable"] as const;
export type InboundFlagState = (typeof inboundFlagState)[number];

const inboundDashboardReturnSchema = z.object({
  id: z.string(),
  orderName: z.string().nullable(),
  merchantTeamId: z.string().nullable(),
  merchantName: z.string().nullable(),
  deliveredAt: z.string().nullable(),
  waitingHours: z.number().nullable(),
  /** One per return label still in flight; a multi-box return has several. */
  estDeliveryDates: z.array(z.string()),
  sortedAt: z.string().nullable(),
  /** Set on `missing` rows only. */
  flagState: z.enum(inboundFlagState).optional(),
});

export type InboundDashboardReturn = z.infer<
  typeof inboundDashboardReturnSchema
>;

export const getInboundDashboardReturns = {
  name: "getInboundDashboardReturns" as const,
  input: z
    .object({
      view: z.enum(inboundDashboardReturnView),
      /** Required when `view` is `coming`. */
      comingKey: inboundComingKeySchema.optional(),
      /** Keeps relative coming buckets stable across a paginated request. */
      comingToday: z.iso.date().optional(),
      /** Narrows `waiting`. */
      ageBucket: z.enum(inboundWaitingAgeBucket).optional(),
      /** Orders by expected date, event time, or time waiting, depending on `view`. */
      sort: z.enum(SortDirection).optional(),
      teamIds: z.array(z.string()).optional(),
      cursor: OpensearchPaginationCursorSchema.optional(),
      page: z.number().int().positive(),
      pageSize: z
        .number()
        .int()
        .positive()
        .max(INBOUND_DASHBOARD_RETURNS_MAX_PAGE_SIZE),
    })
    .refine(({ view, comingKey }) => view !== "coming" || !!comingKey, {
      message: "comingKey is required when view is coming",
      path: ["comingKey"],
    }),
  output: z.object({
    rows: z.array(inboundDashboardReturnSchema),
    total: z.number(),
    nextCursor: OpensearchPaginationCursorSchema.nullable(),
    generatedAt: z.string(),
  }),
};
